import ReactDataGrid from '@inovua/reactdatagrid-community';
import filter from '@inovua/reactdatagrid-community/filter';
import '@inovua/reactdatagrid-community/index.css';
import { Checkbox, Text } from '@mantine/core';
import { useCallback, useEffect, useState } from 'react';
import FailedLoadingScreen from './FailedLoadingScreen';
import LoadingScreen from './LoadingScreen';

// https://reactdatagrid.io/docs/filtering#filter-types-&-operators
const defaultFilterValue = [
  { name: 'code', operator: 'contains', type: 'string', value: '' },
  { name: 'name', operator: 'contains', type: 'string', value: '' },
  { name: 'taxId', operator: 'contains', type: 'string', value: '' },
  {
    name: 'bankAccountNumber',
    operator: 'contains',
    type: 'string',
    value: '',
  },
  { name: 'phone', operator: 'contains', type: 'string', value: '' },
  { name: 'email', operator: 'contains', type: 'string', value: '' },
];

const useFilteredVendors = (vendors, filterValue) => {
  const applyFilter = useCallback(() => {
    if (filterValue.every((f) => f.value === '')) {
      // Restore original data if all filters are empty
      return vendors;
    } else {
      // Apply filters to the vendors
      return filter(vendors, filterValue);
    }
  }, [vendors, filterValue]);

  return applyFilter;
};

function isValidDate(date) {
  if (date instanceof Date) {
    return !isNaN(date.getTime());
  } else if (typeof date === 'string') {
    const parsedDate = new Date(date);
    return !isNaN(parsedDate.getTime());
  }
  return false;
}

function VendorsList({ vendors, error, loading, onVendorSelected, onEdit }) {
  const [filteredVendors, setFilteredVendors] = useState(vendors);
  const [filterValue, setFilterValue] = useState(defaultFilterValue);

  // Centralized filter logic
  const applyFilter = useFilteredVendors(vendors, filterValue);

  const gridColumns = [
    {
      name: 'preferred',
      header: 'Preferred',
      maxWidth: 104,
      editable: false,
      render: ({ value, data }) => {
        return (
          <Checkbox
            checked={!!value}
            onChange={(event) => {
              const { _id, taxId, bankAccountNumber, phone } = data;

              onVendorSelected({
                vendorId: _id,
                taxId: taxId,
                bankAccountNumber: bankAccountNumber,
                phone: phone,
                preferred: event.target.checked,
              });
            }}
          />
        );
      },
    },
    {
      name: 'code',
      header: 'Vendor Code',
      defaultFlex: 1,
      minWidth: 80,
    },
    {
      name: 'name',
      header: 'Vendor Name',
      defaultFlex: 2,
      minWidth: 100,
    },
    {
      name: 'taxId',
      header: 'VAT #',
      defaultFlex: 1,
      minWidth: 80,
    },
    {
      name: 'bankAccountNumber',
      header: 'Bank Account #',
      defaultFlex: 1,
      minWidth: 80,
    },
    {
      name: 'phone',
      header: 'Phone',
      defaultFlex: 1,
      minWidth: 80,
    },
    {
      name: 'email',
      header: 'Email',
      defaultFlex: 1,
      minWidth: 100,
    },
    {
      name: 'updatedAt',
      header: 'Last Updated',
      defaultFlex: 1,
      minWidth: 100,
      render: ({ value }) => {
        return (
          <Text>
            {value && isValidDate(value)
              ? new Date(value).toISOString().substring(0, 10)
              : ''}
          </Text>
        );
      },
    },
  ];

  useEffect(() => {
    setFilteredVendors(applyFilter());
  }, [applyFilter]);

  const onFilterValueChange = useCallback(
    (newFilterValue) => {
      setFilterValue(newFilterValue);
      setFilteredVendors(applyFilter());
    },
    [applyFilter],
  );

  if (loading) {
    return <LoadingScreen height="calc(100dvh - 206px)" />;
  }

  if (error) {
    return <FailedLoadingScreen height="calc(100dvh - 206px)" />;
  }

  return (
    <ReactDataGrid
      idProperty="_id"
      style={{ minHeight: 'calc(100dvh - 206px)' }}
      onFilterValueChange={onFilterValueChange}
      filterValue={filterValue}
      editable={true}
      onEditComplete={onEdit}
      columns={gridColumns}
      dataSource={filteredVendors}
      pagination={true}
      defaultLimit={15}
      pageSizes={[5, 10, 15, 20]}
    />
  );
}

export default VendorsList;
